import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import MovieCard from '@/components/base/MovieCard';
import { heroMovies, newMovies, hotMovies, movies, categories } from '@/mocks/movies';
import type { Movie } from '@/mocks/movies';

export default function Home() {
  const [currentHero, setCurrentHero] = useState(0);
  const heroList = heroMovies;

  const nextHero = useCallback(() => {
    setCurrentHero((prev) => (prev + 1) % heroList.length);
  }, [heroList.length]);

  useEffect(() => {
    const timer = setInterval(nextHero, 5000);
    return () => clearInterval(timer);
  }, [nextHero]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const heroMovie: Movie = heroList[currentHero];

  // SEO Schema
  const schema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Organization',
        name: '麻花影院',
        url: 'https://mahua-cinema.readdy.ai',
        description: '麻花影院聚合最新电影高清大片资源，包含热门动作片、科幻片、喜剧电影等，免费在线观看更新不断。',
      },
      {
        '@type': 'WebSite',
        name: '麻花影院',
        url: 'https://mahua-cinema.readdy.ai',
        potentialAction: {
          '@type': 'SearchAction',
          target: 'https://mahua-cinema.readdy.ai/search?q={search_term_string}',
          'query-input': 'required name=search_term_string',
        },
      },
      {
        '@type': 'WebPage',
        name: '麻花影院 - 首页',
        url: 'https://mahua-cinema.readdy.ai',
        isPartOf: { '@type': 'WebSite', name: '麻花影院' },
      },
    ],
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
      />
      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main>
          {/* Hero Section */}
          <section className="relative w-full h-[420px] md:h-[600px] overflow-hidden">
            <div
              className="absolute inset-0 bg-cover bg-center transition-all duration-700"
              style={{ backgroundImage: `url(${heroMovie.image})` }}
            >
              <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-background-50"></div>
            </div>

            <div className="absolute inset-0 flex items-end pb-10 md:pb-16">
              <div className="w-full px-4 md:px-6">
                <div className="max-w-7xl mx-auto">
                  <div className="flex flex-col md:flex-row md:items-end gap-6">
                    <div className="flex-1">
                      <span className="inline-block bg-accent-500 text-background-950 text-xs font-bold px-3 py-1 rounded-md mb-3 whitespace-nowrap">
                        本周热门
                      </span>
                      <h1 className="font-heading text-3xl md:text-5xl font-bold text-white mb-2">
                        {heroMovie.title}
                      </h1>
                      <p className="text-sm md:text-base text-foreground-600 max-w-xl line-clamp-2">
                        {heroMovie.description}
                      </p>
                      <div className="flex items-center gap-4 mt-3">
                        <div className="flex items-center gap-1">
                          <div className="w-5 h-5 flex items-center justify-center">
                            <i className="ri-star-fill text-accent-500"></i>
                          </div>
                          <span className="text-accent-400 font-bold text-lg">{heroMovie.rating}</span>
                        </div>
                        <span className="text-foreground-500 text-sm">{heroMovie.year}</span>
                        <span className="text-foreground-500 text-sm">{heroMovie.duration}</span>
                        <span className="text-foreground-500 text-sm">{heroMovie.categoryName}</span>
                      </div>
                      <Link
                        to={`/movie/${heroMovie.id}`}
                        className="inline-flex items-center gap-2 mt-4 bg-primary-500 text-background-50 px-6 py-2.5 rounded-lg font-semibold text-sm hover:bg-primary-600 transition-all cursor-pointer whitespace-nowrap"
                      >
                        <div className="w-4 h-4 flex items-center justify-center">
                          <i className="ri-play-fill"></i>
                        </div>
                        立即观看
                      </Link>
                    </div>

                    <div className="hidden md:flex gap-3">
                      {heroList.map((m, i) => (
                        <button
                          key={m.id}
                          onClick={() => setCurrentHero(i)}
                          className={`w-20 h-28 rounded-lg overflow-hidden transition-all cursor-pointer ${
                            i === currentHero
                              ? 'ring-2 ring-accent-500 scale-105'
                              : 'opacity-60 hover:opacity-100'
                          }`}
                        >
                          <img src={m.image} alt={m.title} className="w-full h-full object-cover" loading="lazy" />
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Hero dots */}
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 md:hidden">
              {heroList.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setCurrentHero(i)}
                  className={`w-2 h-2 rounded-full transition-all cursor-pointer ${
                    i === currentHero ? 'bg-accent-500 w-6' : 'bg-foreground-400'
                  }`}
                />
              ))}
            </div>
          </section>

          {/* Category Navigation */}
          <section className="px-4 md:px-6 py-6 md:py-8">
            <div className="max-w-7xl mx-auto">
              <div className="flex items-center gap-3 overflow-x-auto pb-2 scrollbar-hide flex-wrap">
                {categories.map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    className="flex items-center gap-1.5 bg-background-100 hover:bg-background-200 text-foreground-700 hover:text-foreground-950 px-4 py-2 rounded-full text-sm transition-all cursor-pointer whitespace-nowrap"
                  >
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className={`${cat.icon} text-sm`}></i>
                    </div>
                    {cat.name}
                  </Link>
                ))}
              </div>
            </div>
          </section>

          {/* Latest Movies */}
          <section className="px-4 md:px-6 py-6 md:py-10">
            <div className="max-w-7xl mx-auto">
              <div className="flex items-center justify-between mb-5">
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">
                  <Link to="/category/kehuan" className="hover:text-primary-400 transition-colors cursor-pointer">最新上线</Link>
                </h2>
                <Link
                  to="/category/kehuan"
                  className="text-sm text-foreground-500 hover:text-primary-400 transition-colors cursor-pointer whitespace-nowrap"
                >
                  查看全部 <i className="ri-arrow-right-line ml-1"></i>
                </Link>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
                {newMovies.slice(0, 6).map((movie) => (
                  <MovieCard key={movie.id} movie={movie} />
                ))}
              </div>
            </div>
          </section>

          {/* Hot Movies */}
          <section className="px-4 md:px-6 py-6 md:py-10" id="hot">
            <div className="max-w-7xl mx-auto">
              <div className="flex items-center justify-between mb-5">
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">
                  热门推荐
                </h2>
                <Link
                  to="/category/dongzuo"
                  className="text-sm text-foreground-500 hover:text-primary-400 transition-colors cursor-pointer whitespace-nowrap"
                >
                  查看全部 <i className="ri-arrow-right-line ml-1"></i>
                </Link>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                {hotMovies.slice(0, 5).map((movie) => (
                  <MovieCard key={movie.id} movie={movie} size="large" />
                ))}
              </div>
            </div>
          </section>

          {/* All Movies Grid */}
          <section className="px-4 md:px-6 py-6 md:py-10">
            <div className="max-w-7xl mx-auto">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-5">
                全部影片
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
                {movies.map((movie) => (
                  <MovieCard key={movie.id} movie={movie} />
                ))}
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}